import { CONFIG } from '../config';
import { publishedPropertyContentByLocale, publishedPropertyFingerprints } from '../i18n/publishedBundles.js';
import { syncPublishedPropertyContentIfNeeded } from './cmsContentRemoteSync.js';

// Single-flight per locale for public property payload.
const inFlightByLocale = new Map();
const cachedByLocale = new Map();

/**
 * Returns the latest public property payload for a locale, deduping concurrent callers.
 * On fresh environments, payload may be `{ success: true, property: null, floors: [] }`.
 */
export async function getPublicPropertyPayload(locale) {
  const cached = cachedByLocale.get(locale);
  if (cached) return cached;

  const existing = inFlightByLocale.get(locale);
  if (existing) return existing;

  const p = (async () => {
    try {
      // Prefer version/fingerprint sync first (single-flight /property/version and /property/:locale).
      const bundledFp = publishedPropertyFingerprints[locale] || '';
      const bundledContent = publishedPropertyContentByLocale?.[locale] || null;
      let out = null;
      await syncPublishedPropertyContentIfNeeded({
        locale,
        bundledFingerprint: bundledFp,
        bundledContent,
        onUpdate: (_loc, body) => {
          out = body;
        }
      });

      // If sync produced content (either bundled baseline or fetched remote body), use that.
      if (out && typeof out === 'object') {
        cachedByLocale.set(locale, out);
        return out;
      }

      // Fallback: if we cannot do fingerprint checks (e.g. missing bundled fingerprint),
      // fetch directly once.
      const res = await fetch(`${CONFIG.API_BASE_URL}/property/${locale}`, {
        headers: { Accept: 'application/json' },
        cache: 'no-store'
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      if (!data.success) throw new Error(data.error || 'No property content for this language');
      cachedByLocale.set(locale, data);
      return data;
    } finally {
      inFlightByLocale.delete(locale);
    }
  })();

  inFlightByLocale.set(locale, p);
  return p;
}

export function clearPublicPropertyPayloadCache() {
  cachedByLocale.clear();
  inFlightByLocale.clear();
}

